import { ChangeEvent, useEffect, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { DataTablesQueryModel } from "../../interfaces/dataTables";
import NoDataTableBody from "../../components/NoDataTableBody";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import { restoreAlertforSession } from "../../components/BroadcastAlerts";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import {
	useGetMyAlertsQuery,
	useHideAlertsMutation,
	useHideAllAlertsMutation,
	useRestoreAlertMutation,
} from "./alertsApiSlice";

export interface Props {
	className?: string;
}

interface AlertModel {
	id: number;
	message: string;
	expireDate: string;
	isHidden: boolean;
	isChecked: boolean;
}

export default function Alerts(props: Props) {
	const [view, setView] = useState<string>("table");
	const [alerts, setAlerts] = useState<AlertModel[]>([]);
	const [anyChecked, setAnyChecked] = useState(false);
	const [allChecked, setAllChecked] = useState(false);

	const { userId } = useAppSelector((state) => state.user);

	const q: DataTablesQueryModel = {
		iDisplayStart: 0,
		iDisplayLength: 100,
	};

	const result = useGetMyAlertsQuery(q, { refetchOnMountOrArgChange: true });
	const data = result.data;

	const [callRestoreAlert] = useRestoreAlertMutation();
	const [callHideAlerts] = useHideAlertsMutation();
	const [callHideAllAlerts] = useHideAllAlertsMutation();

	useEffect(() => {
		if (!data || data.iTotalDisplayRecords === 0) {
			setAlerts([]);
			return;
		}

		const alerts: AlertModel[] = data.aaData.map((value) => {
			return {
				id: Number(value.dT_RowId),
				message: value.message,
				expireDate: value.expireDate,
				isHidden: value.isRead,
				isChecked: false,
			};
		});

		setAlerts(alerts);
	}, [data]);

	useEffect(() => {
		setAllChecked(alerts.length > 0 && alerts.every((alert) => alert.isChecked));
		setAnyChecked(
			alerts.length > 0 && alerts.some((alert) => alert.isChecked && !alert.isHidden)
		);
	}, [alerts]);

	function handleSelectOne(e: ChangeEvent<HTMLInputElement>) {
		const id = Number(e.currentTarget.value);
		const _alerts = alerts.map((alert) => {
			if (alert.id === id) {
				return { ...alert, isChecked: e.currentTarget.checked };
			}
			return alert;
		});
		setAlerts(_alerts);
	}

	function handleSelectAll(isChecked: boolean) {
		setAlerts(
			alerts.map((alert) => {
				return { ...alert, isChecked };
			})
		);
	}

	function handleHideAlerts() {
		if (allChecked) {
			callHideAllAlerts()
				.unwrap()
				.then(() => {
					setAlerts(
						alerts.map((alert) => {
							return { ...alert, isChecked: false, isHidden: true };
						})
					);
				})
				.catch((error: any) => {
					console.log(error);
				});
		} else {
			const alertsToHide = alerts.filter((alert) => alert.isChecked).map((alert) => alert.id);
			callHideAlerts(alertsToHide)
				.unwrap()
				.then(() => {
					setAlerts(
						alerts.map((alert) => {
							return {
								...alert,
								isChecked: false,
								isHidden: alert.isHidden || alert.isChecked,
							};
						})
					);
				})
				.catch((error: any) => {
					console.log(error);
				});
		}
	}

	function handleClickHidden(alert: AlertModel) {
		if (!alert.isHidden) return;

		callRestoreAlert(alert.id)
			.unwrap()
			.then(() => {
				setAlerts(
					alerts.map((_alert) => {
						return alert.id === _alert.id ? { ..._alert, isHidden: false } : _alert;
					})
				);
				restoreAlertforSession(userId, alert.id);
			})
			.catch((error: any) => {
				console.log(error);
			});
	}

	return (
		<main className={props.className}>
			<section className="pb-4">
				<div className="flex justify-between pb-8">
					<h1>Manage Alerts</h1>
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						options={[
							{ value: "table", label: "Tables" },
							{ value: "raw", label: "Raw Data" },
						]}
						fieldClasses="w-64"
					/>
				</div>
				<p>
					On this page you can hide alerts that you no longer wish to see when you log in.
					To restore an alert you have hidden, click its check mark.
				</p>
			</section>
			{view == "table" && (
				<section className="shadow-sm overflow-hidden">
					<div className="flex justify-between items-end mb-2">
						<div></div>
						<div>
							<button
								className="btn btn-primary"
								disabled={!anyChecked}
								onClick={() => handleHideAlerts()}>
								Hide Selected
							</button>
						</div>
					</div>
					<table className="tbl tbl-data">
						<thead>
							<tr>
								<th>
									<input
										type="checkbox"
										className="h-4 w-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700"
										onChange={(e) => handleSelectAll(e.target.checked)}
										checked={allChecked}
									/>
								</th>
								<th>Expires On</th>
								<th>Message</th>
								<th className="!text-center w-24">Hidden</th>
							</tr>
						</thead>

						{result.isFetching ? (
							<tbody>
								<tr>
									<td colSpan={6} className="!text-center p-1">
										Loading...
									</td>
								</tr>
							</tbody>
						) : alerts.length == 0 ? (
							<NoDataTableBody
								columnCount={6}
								message="You have no alerts at this time."
							/>
						) : (
							<tbody>
								{alerts.map((alert, index) => {
									return (
										<tr key={"alert-" + alert.id} className="">
											<td>
												<input
													value={alert.id}
													checked={alert.isChecked}
													type="checkbox"
													disabled={alert.isHidden}
													className="h-4 w-4 rounded-sm border-gray-300 text-primary-700 focus:ring-primary-700  disabled:bg-gray-100 disabled:checked:bg-gray-100 disabled:checked:border-gray-300"
													onChange={(e) => handleSelectOne(e)}
												/>
											</td>
											<td>{alert.expireDate}</td>
											<td>{alert.message}</td>
											{alert.isHidden ? (
												<td
													className="!text-center font-bold cursor-pointer"
													onClick={(e) => handleClickHidden(alert)}>
													✓
												</td>
											) : (
												<td></td>
											)}
										</tr>
									);
								})}
							</tbody>
						)}
					</table>
				</section>
			)}
			{view == "raw" && (
				<section>
					<RtkqResultViewer heading="Alerts" result={result} />
				</section>
			)}
		</main>
	);
}
